import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUploadAIChatFilesMutation } from '../../../../queries/Attachments';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { darkGrayFontColor } from '../../../styles/colors';
import {
	Attachment,
	AttachmentHoverCard,
	AttachmentHoverCardContent,
	AttachmentHoverCardTrigger,
	AttachmentInfo,
	AttachmentPreview,
	AttachmentRemove,
	Attachments,
	getAttachmentLabel,
	getMediaCategory,
} from '../Attachments';
import { PromptInputHeader, usePromptInputAttachments } from '.';

// Shared defaults for AI chat file attachment composers. Matches backend allowlist
// (Levitate-API #11342) and request-size cap of 20MB.
export const AI_CHAT_FILE_ACCEPT =
	'.pdf,.png,.jpg,.jpeg,.webp,.txt,.md,.csv,application/pdf,image/png,image/jpeg,image/webp,text/plain,text/markdown,text/csv';
export const AI_CHAT_MAX_FILES = 5;
export const AI_CHAT_MAX_FILE_SIZE = 20_000_000;

export type UseAIChatAttachmentsResult = {
	/** Map of PromptInput-assigned local file id → server-side uploaded attachment. */
	uploadedById: Record<string, Api.IFileAttachmentWithURL>;
	/** Local file ids whose upload is currently in flight. */
	pendingIds: ReadonlySet<string>;
	/** True while any upload is in flight. */
	isUploading: boolean;
	/** Start an upload for a local file. Returns the uploaded attachment, or null on failure. */
	upload: (localId: string, file: File) => Promise<Api.IFileAttachmentWithURL | null>;
	/** Forget a local id (called when the user removes a pill before send). */
	forget: (localId: string) => void;
	/** Clear all state after a successful submit. */
	reset: () => void;
};

/**
 * Owns the upload lifecycle for files attached to an AI chat composer.
 *
 * Lives in the parent (e.g. ChatConversationBuilder); paired with the
 * {@link PromptInputAttachments} watcher component below, which lives
 * inside PromptInput's children, observes the PromptInput file list,
 * and drives the controller via {@link UseAIChatAttachmentsResult.upload}.
 * Parent reads {@link UseAIChatAttachmentsResult.uploadedById} on submit
 * to build the AIFileContent[] for the outgoing message.
 */
export function useAIChatAttachments({
	impersonationContext,
}: {
	impersonationContext?: Api.IImpersonationContext;
} = {}): UseAIChatAttachmentsResult {
	const uploadMutation = useUploadAIChatFilesMutation();
	const [uploadedById, setUploadedById] = useState<Record<string, Api.IFileAttachmentWithURL>>({});
	const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());
	// Local ids whose upload was canceled (via forget) while still in flight.
	// Checked after the await to drop the late success write that would otherwise
	// re-add a file the user already removed from the composer.
	const canceledRef = useRef<Set<string>>(new Set());

	const upload = useCallback(
		async (localId: string, file: File): Promise<Api.IFileAttachmentWithURL | null> => {
			canceledRef.current.delete(localId);
			setPendingIds(prev => {
				const next = new Set(prev);
				next.add(localId);
				return next;
			});
			try {
				const result = await uploadMutation.mutateAsync({ files: [file], impersonationContext });
				if (canceledRef.current.has(localId)) {
					canceledRef.current.delete(localId);
					return null;
				}
				const fileAttachment = result.succeeded?.[0];
				if (fileAttachment) {
					setUploadedById(prev => ({ ...prev, [localId]: fileAttachment }));
					return fileAttachment;
				}
				return null;
			} catch {
				return null;
			} finally {
				setPendingIds(prev => {
					if (!prev.has(localId)) return prev;
					const next = new Set(prev);
					next.delete(localId);
					return next;
				});
			}
		},
		[uploadMutation, impersonationContext]
	);

	const forget = useCallback((localId: string) => {
		canceledRef.current.add(localId);
		setUploadedById(prev => {
			if (!(localId in prev)) return prev;
			const { [localId]: _removed, ...rest } = prev;
			return rest;
		});
		setPendingIds(prev => {
			if (!prev.has(localId)) return prev;
			const next = new Set(prev);
			next.delete(localId);
			return next;
		});
	}, []);

	const reset = useCallback(() => {
		canceledRef.current.clear();
		setUploadedById({});
		setPendingIds(new Set());
	}, []);

	return {
		uploadedById,
		pendingIds,
		isUploading: pendingIds.size > 0,
		upload,
		forget,
		reset,
	};
}

export type PromptInputAttachmentsProps = {
	controller: UseAIChatAttachmentsResult;
};

/**
 * Renders the composer's attached files above the PromptInput textarea.
 *
 * Watches PromptInput's local file list; for any file we haven't seen,
 * kicks off an upload via the controller. Failed uploads are removed
 * from PromptInput's list and a toast is surfaced.
 *
 * Renders an uploading skeleton (with spinner overlay for images) while
 * the upload is in flight, then swaps to an Attachments grid tile once
 * the upload succeeds.
 */
export const PromptInputAttachments = ({ controller }: PromptInputAttachmentsProps) => {
	const attachments = usePromptInputAttachments();
	const { files, remove } = attachments;
	const { uploadedById, pendingIds, upload, forget } = controller;
	const startedRef = useRef<Set<string>>(new Set());

	useEffect(() => {
		const currentIds = new Set(files.map(f => f.id));

		// Kick off uploads for any new files we haven't started yet.
		const newFiles = files.filter(f => !startedRef.current.has(f.id) && f.file);
		if (newFiles.length > 0) {
			newFiles.forEach(f => startedRef.current.add(f.id));
			Promise.all(
				newFiles.map(async f => {
					const result = await upload(f.id, f.file!);
					return { id: f.id, result };
				})
			).then(results => {
				const failed = results.filter(r => r.result === null);
				if (failed.length > 0) {
					toast.error(`Failed to upload ${failed.length} file${failed.length === 1 ? '' : 's'}`);
					failed.forEach(({ id }) => remove(id));
				}
			});
		}

		// Drop bookkeeping for files that have been removed from PromptInput's list.
		startedRef.current.forEach(id => {
			if (!currentIds.has(id)) {
				startedRef.current.delete(id);
				forget(id);
			}
		});
	}, [files, upload, forget, remove]);

	if (files.length === 0) {
		return null;
	}

	return (
		<PromptInputHeader>
			<Attachments variant='inline' styles={[s.attachmentsOverride]}>
				{files.map(file => {
					const uploaded = uploadedById[file.id];
					const isPending = pendingIds.has(file.id);
					const mediaCategory = getMediaCategory(file);
					const label = getAttachmentLabel(file);
					if (!uploaded || isPending) {
						return (
							<Attachment key={file.id} data={file}>
								<div className={css(s.attachmentChipContainer, s.uploadingSpinnerSlot)}>
									<LoadingSpinner type='tiny' />
								</div>
								<AttachmentInfo />
							</Attachment>
						);
					}
					return (
						<AttachmentHoverCard key={file.id}>
							<AttachmentHoverCardTrigger asChild>
								<Attachment data={file} onRemove={() => remove(file.id)} styles={[s.attachmentOverride]}>
									<div className={css(s.attachmentChipContainer)}>
										<div className={css(s.attachmentPreviewContainer)}>
											<AttachmentPreview />
										</div>
										<AttachmentRemove styles={[s.attachmentRemove]} />
									</div>
									<AttachmentInfo />
								</Attachment>
							</AttachmentHoverCardTrigger>
							<AttachmentHoverCardContent>
								<div className={css(s.attachmentHoverCardContentContainer)}>
									{mediaCategory === 'image' && file.type === 'file' && file.url && (
										<div className={css(s.attachmentHoverCardContentImageContainer)}>
											<img
												alt={label}
												className={css(s.attachmentHoverCardContentImage)}
												height={320}
												src={file.url}
												width={267}
											/>
										</div>
									)}
									<div className={css(s.attachmentHoverCardContentDesc)}>
										<h4 className={css(s.attachmentHoverCardContentDescHeading)}>{label}</h4>
										{file.mediaType && (
											<p className={css(s.attachmentHoverCardContentDescSubtitle)}>{file.mediaType}</p>
										)}
									</div>
								</div>
							</AttachmentHoverCardContent>
						</AttachmentHoverCard>
					);
				})}
			</Attachments>
		</PromptInputHeader>
	);
};

const s = StyleSheet.create({
	uploadingSpinnerSlot: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	attachmentsOverride: {
		marginLeft: 0,
	},
	attachmentOverride: {
		'--attachment-chip-preview-opacity': '1',
		':hover': {
			'--attachment-chip-preview-opacity': '0',
		},
	},
	attachmentChipContainer: {
		boxSizing: 'border-box',
		position: 'relative',
		width: 20,
		height: 20,
		flexShrink: 0,
	},
	attachmentPreviewContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		transition: 'opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
		// @ts-ignore
		opacity: 'var(--attachment-chip-preview-opacity)',
	},
	attachmentRemove: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
	},
	attachmentHoverCardContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: 12,
		boxSizing: 'border-box',
	},
	attachmentHoverCardContentImageContainer: {
		display: 'flex',
		maxHeight: 280,
		width: 233,
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		borderRadius: 6,
	},
	attachmentHoverCardContentImage: {
		maxHeight: '100%',
		maxWidth: '100%',
		objectFit: 'contain',
	},
	attachmentHoverCardContentDesc: {
		boxSizing: 'border-box',
		paddingLeft: 2,
		paddingRight: 2,
	},
	attachmentHoverCardContentDescHeading: {
		fontSize: 14,
		fontWeight: 'bold',
		lineHeight: 1,
		margin: 0,
		padding: 0,
		boxSizing: 'border-box',
	},
	attachmentHoverCardContentDescSubtitle: {
		color: darkGrayFontColor,
		fontSize: 12,
		padding: 0,
		margin: 0,
		boxSizing: 'border-box',
		marginTop: 4,
	},
});
